import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';

const base = process.env.PUBLIC_URL || '';

const BRAND_LOGOS = {
  light: {
    src: `${base}/assets/brand/logos/icom-greece-logo-light.png`,
    srcSet: `${base}/assets/brand/logos/icom-greece-logo-light.png 1x, ${base}/assets/brand/logos/icom-greece-logo-light@2x.png 2x`,
  },
  dark: {
    src: `${base}/assets/brand/logos/icom-greece-logo-dark.png`,
    srcSet: `${base}/assets/brand/logos/icom-greece-logo-dark.png 1x, ${base}/assets/brand/logos/icom-greece-logo-dark@2x.png 2x`,
  },
} as const;

const Content = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 2;
  position: relative;
  padding: ${spacing[6]};

  ${breakpoints.up.md} {
    padding: ${spacing[8]};
    align-items: center;
    text-align: center;
  }
`;

const LogoWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing[8]};
`;

const LogoImg = styled.img`
  width: 100%;
  max-width: 260px;
  height: auto;
  object-fit: contain;
  display: block;
`;

const DefinitionBlock = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  max-height: calc(100vh - 73px - 280px);
  padding-right: ${spacing[2]};
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.overlayLight || 'rgba(0,0,0,0.04)'};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
`;

const DefinitionCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[5]};
  width: 100%;
  padding: ${spacing[4]} ${spacing[5]};
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  border-left: 3px solid ${props => props.theme.colors.primary};
  box-shadow: ${props => props.theme.colors.shadow ? `0 2px 12px ${props.theme.colors.shadow}` : '0 2px 12px rgba(0,0,0,0.05)'};

  ${breakpoints.down.sm} {
    grid-template-columns: 1fr;
  }
`;

const DefinitionColumn = styled.div`
  min-width: 0;
`;

const DefinitionParagraph = styled.p`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.relaxed};
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-style: italic;
`;

const LanguageLabel = styled.span`
  display: block;
  font-family: ${typography.fontFamily.primary};
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: ${typography.letterSpacing.wide};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[2]};
  font-style: normal;
`;

const MUSEUM_DEFINITION_EN =
  'A museum is a not-for-profit, permanent institution in the service of society that researches, collects, conserves, interprets and exhibits tangible and intangible heritage. Open to the public, accessible and inclusive, museums foster diversity and sustainability. They operate and communicate ethically, professionally and with the participation of communities, offering varied experiences for education, enjoyment, reflection and knowledge sharing.';

const MUSEUM_DEFINITION_GR =
  'Το Μουσείο είναι ένας μόνιμος, μη κερδοσκοπικός οργανισμός, στην υπηρεσία της κοινωνίας, ο οποίος ερευνά, συλλέγει, συντηρεί, ερμηνεύει και εκθέτει τεκμήρια υλικής και άυλης κληρονομιάς. Προσβάσιμα στο κοινό, χωρίς αποκλεισμούς, τα μουσεία προάγουν την ποικιλομορφία και την αειφορία. Λειτουργούν και επικοινωνούν με επαγγελματική δεοντολογία και με τη συμμετοχή των κοινοτήτων, προσφέροντας ποικίλες εμπειρίες με σκοπό την εκπαίδευση, την ψυχαγωγία, τον αναστοχασμό και τη διάδοση της γνώσης.';

interface AuthLeftPanelProps {
  /** When false, only the brand logo is shown (register flows). Default true for login. */
  showDefinition?: boolean;
}

/**
 * Left panel for Login and Register: brand wordmark, optionally with ICOM museum definition (EN | GR).
 */
const AuthLeftPanel: React.FC<AuthLeftPanelProps> = ({ showDefinition = true }) => {
  const { mode } = useTheme();
  const logo = BRAND_LOGOS[mode];

  return (
    <Content>
      <LogoWrapper>
        <LogoImg
          src={logo.src}
          srcSet={logo.srcSet}
          width={790}
          height={169}
          alt="ICOM Greece"
        />
      </LogoWrapper>
      {showDefinition && (
        <DefinitionBlock>
          <DefinitionCard>
            <DefinitionColumn>
              <LanguageLabel>English</LanguageLabel>
              <DefinitionParagraph>{MUSEUM_DEFINITION_EN}</DefinitionParagraph>
            </DefinitionColumn>
            <DefinitionColumn>
              <LanguageLabel>Ελληνικά</LanguageLabel>
              <DefinitionParagraph>{MUSEUM_DEFINITION_GR}</DefinitionParagraph>
            </DefinitionColumn>
          </DefinitionCard>
        </DefinitionBlock>
      )}
    </Content>
  );
};

export default AuthLeftPanel;
